/**
 * @ site tags (16 Oct 2026). The composer's `@` picker lists the sites Operations manages
 * (from GET /api/site-tags); picking one inserts `@handle` (the site's own host label,
 * e.g. azlservices.com → @azlservices). The backend resolves every tag for the agent.
 */
export interface SiteTag {
  handle: string;
  id: string;
  name: string;
  host: string;
  url: string;
  kind: "client" | "sample";
}
export interface SitePreview { favicon: string | null; image: string | null; title: string | null }

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

let tagsCache: { at: number; tags: SiteTag[] } | null = null;
let tagsInFlight: Promise<SiteTag[]> | null = null;
const previews = new Map<string, SitePreview | "loading">();
const listeners = new Set<() => void>();

export function onSiteTagsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
const emit = () => listeners.forEach((fn) => fn());

export function cachedSiteTags(): SiteTag[] { return tagsCache?.tags ?? []; }

export async function loadSiteTags(fetcher: Fetcher, force = false): Promise<SiteTag[]> {
  if (!force && tagsCache && Date.now() - tagsCache.at < 5 * 60_000) return tagsCache.tags;
  if (tagsInFlight) return tagsInFlight;
  tagsInFlight = (async () => {
    try {
      const r = await fetcher("/api/site-tags");
      if (!r.ok) return tagsCache?.tags ?? [];
      const body = await r.json();
      const tags: SiteTag[] = Array.isArray(body?.tags) ? body.tags : [];
      tagsCache = { at: Date.now(), tags };
      emit();
      return tags;
    } catch { return tagsCache?.tags ?? []; }
    finally { tagsInFlight = null; }
  })();
  return tagsInFlight;
}

export function sitePreview(id: string): SitePreview | null {
  const p = previews.get(id);
  return p && p !== "loading" ? p : null;
}
export function loadSitePreview(fetcher: Fetcher, id: string): void {
  if (previews.has(id)) return;
  previews.set(id, "loading");
  fetcher(`/api/site-tags/${encodeURIComponent(id)}/preview`)
    .then((r) => (r.ok ? r.json() : null))
    .then((b) => { previews.set(id, { favicon: b?.favicon ?? null, image: b?.image ?? null, title: b?.title ?? null }); emit(); })
    .catch(() => { previews.set(id, { favicon: null, image: null, title: null }); emit(); });
}

/** "AnZhiLu Transportation Services - 8261 1112 - 21/9" → "AnZhiLu Transportation Services". */
export function tagDisplayName(t: Pick<SiteTag, "name" | "host">): string {
  const base = (t.name || "").split(" - ")[0]!.trim();
  return base && !/^new sample/i.test(base) ? base : t.host;
}

// ── smart search (the Desk's): any word order, word starts, one typo, digits ───────────────
const NOISE = new Set(["pte", "ltd", "the", "sg", "singapore", "llp", "co", "com", "www"]);
const words = (s: string, drop = false) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, " ").trim().split(/\s+/)
  .filter((w) => w && !(drop && NOISE.has(w)));
function near(a: string, b: string): boolean {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0, j = 0, edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) { i++; j++; continue; }
    if (++edits > 1) return false;
    if (a.length > b.length) i++; else if (b.length > a.length) j++; else { i++; j++; }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}
export function tagMatches(t: SiteTag, query: string): boolean {
  const q = query.trim();
  if (!q) return true;
  const hay = [...words(`${t.handle} ${tagDisplayName(t)} ${t.host} ${t.name}`)];
  const digits = q.replace(/\D/g, "");
  if (digits.length >= 4 && t.name.replace(/\D/g, "").includes(digits)) return true;
  const qs = words(q, true);
  if (!qs.length) return true;
  return qs.every((w) => hay.some((h) => h.startsWith(w) || (w.length >= 5 && near(w, h.slice(0, w.length + 1)) ) || (w.length >= 5 && near(w, h))));
}
export function filterSiteTags(tags: SiteTag[], query: string, limit = 30): SiteTag[] {
  const q = query.toLowerCase();
  const hits = tags.filter((t) => tagMatches(t, query));
  // Exact handle first, then handle prefix, then the rest (already newest-active first).
  const rank = (t: SiteTag) => (t.handle === q ? 0 : t.handle.startsWith(q) ? 1 : 2);
  return hits.sort((a, b) => rank(a) - rank(b)).slice(0, limit);
}

/** Tags written in a draft/message: `@handle` at a word start (never inside an email address). */
const TAG_RE = /(^|[\s([{,;:"'])@([a-z0-9][a-z0-9-]{1,62})(?![a-z0-9-])/gi;
export function tagsInText(text: string): string[] {
  const seen = new Set<string>();
  for (const m of String(text ?? "").matchAll(TAG_RE)) seen.add(m[2]!.toLowerCase());
  return [...seen];
}

/** Markdown for a sent message: known `@handle`s become links to that site's chat. */
export function linkTagsInMarkdown(text: string, tags: SiteTag[]): string {
  if (!tags.length || !text.includes("@")) return text;
  const byHandle = new Map(tags.map((t) => [t.handle, t]));
  // Leave code spans/blocks alone.
  return text.split(/(```[\s\S]*?```|`[^`\n]*`)/).map((part, i) => i % 2 ? part : part.replace(TAG_RE, (m, pre: string, h: string) => {
    const t = byHandle.get(h.toLowerCase());
    return t ? `${pre}[@${t.handle}](https://agent.reputifly.cloud/?chat=${encodeURIComponent(t.id)}&tag=1)` : m;
  })).join("");
}
